/* ──────────────────────────────────────────────────────────────────────
 *  FellowMembersScroller — horizontal scroller for the "Fellow members of
 *  {Org}" discovery feed card (HANDOFF_FEED_MEMBER_DISCOVERY).
 *
 *  Native overflow-x handles trackpad/touch, but desktop mouse users often
 *  can't scroll a horizontal row at all (no trackpad, auto-hidden
 *  scrollbar), so this adds explicit ‹ › arrow controls that appear only
 *  when there's more to see and scroll by ~one card. Keeps the native
 *  scroll + snap for touch.
 * ────────────────────────────────────────────────────────────────────── */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import FellowMemberCard from './FellowMemberCard';
import type { FellowMemberCandidate } from '../../api/discovery';
import styles from './FellowMembersScroller.module.css';

interface Props {
  candidates: FellowMemberCandidate[];
  onDismiss?: (targetGuid: string, kind: 'snooze' | 'ignore') => void;
}

// One card (200px) + row gap (12px).
const STEP = 212;

export default function FellowMembersScroller({ candidates, onDismiss }: Props) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    update();
    const el = rowRef.current;
    if (!el) return;
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [update, candidates.length]);

  const scrollByStep = useCallback((dir: 1 | -1) => {
    rowRef.current?.scrollBy({ left: dir * STEP, behavior: 'smooth' });
  }, []);

  return (
    <div className={styles.wrap}>
      {canLeft && (
        <button
          type="button"
          className={`${styles.arrow} ${styles.left}`}
          onClick={() => scrollByStep(-1)}
          aria-label="Scroll to previous members"
        >
          &lsaquo;
        </button>
      )}

      <div className={styles.row} ref={rowRef}>
        {candidates.map((c) => (
          <FellowMemberCard key={c.targetGuid} candidate={c} onDismiss={onDismiss} />
        ))}
      </div>

      {canRight && (
        <button
          type="button"
          className={`${styles.arrow} ${styles.right}`}
          onClick={() => scrollByStep(1)}
          aria-label="Scroll to more members"
        >
          &rsaquo;
        </button>
      )}
    </div>
  );
}
